import {
  DateFormat,
  NullableDateInput,
  stringifyDate,
} from '@superdispatch/dates';
import { isEmptyRecord } from 'shared/helpers/CommonHelpers';
import { z } from 'zod';
import { contactSchema } from '../../contacts/data/ContactDTO';
import {
  loadExpenseSchema,
  LoadPaymentDTO,
  loadVehicleSchema,
  LoadVehicleDTO,
  loadVenueSchema,
  LOAD_INSPECTION_TYPES,
  orderLoadSchema,
} from './LoadDTO';

const zodString = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

/** Same as {@link zodString}, but rejects empty values. */
function zodRequiredString(error = 'This field is required') {
  return zodString.refine((value) => value.length > 0, error).prefault('');
}

/** Same as {@link zodString}, but rejects values longer than `max` characters. */
function zodMaxString(
  max: number,
  error = `Maximum length is ${max} characters`,
) {
  return z
    .string()
    .max(max, error)
    .nullish()
    .transform((value) => value ?? '');
}

const zodBoolean = z.preprocess(
  (value) =>
    typeof value === 'string' || typeof value === 'number'
      ? /^(true|1)$/i.test(String(value))
      : value,
  z.boolean().nullable().default(false).catch(false),
);

const zodNumber = z.preprocess(
  // `Number('')` is `0`, which would turn a cleared numeric input into a value.
  (value) => (value == null || value === '' ? null : Number(value)),
  z.number().nullable().default(null).catch(null),
);

function zodDateString(format: DateFormat) {
  return z.preprocess(
    (value) => stringifyDate(value as NullableDateInput, { format }),
    z.string().nullable().default(null),
  );
}

/**
 * Same as {@link zodDateString}, but rejects empty values. The empty case is an
 * emptiness check rather than a type error so that it does not abort the
 * object-level checks that follow it.
 */
function zodRequiredDateString(format: DateFormat, error: string) {
  return z
    .preprocess(
      (value) => stringifyDate(value as NullableDateInput, { format }) ?? '',
      z.string().min(1, error),
    )
    .prefault(null);
}

/**
 * Reusable `never` schema that allows to remove properties from a `z.object`.
 */
const zodOmit = z
  .unknown()
  .transform(() => undefined)
  .optional();

//
// LoadDetailsEditDTO
//

export type LoadDetailsEditDTO = z.infer<typeof loadDetailsEditSchema>;
export const loadDetailsEditSchema = z.object({
  number: zodMaxString(50),
  internal_load_id: zodMaxString(25),
  instructions: zodString,
  inspection_type: z
    .enum(LOAD_INSPECTION_TYPES, 'Select method')
    .catch(LOAD_INSPECTION_TYPES[0])
    .default(LOAD_INSPECTION_TYPES[0]),
});

//
// LoadPickupEditDTO
//

export type LoadPickupEditDTO = z.infer<typeof loadPickupEditSchema>;
export const loadPickupEditSchema = z.object({
  notes: zodString,
  venue: loadVenueSchema.prefault({}),
  save_pickup_contact: zodBoolean,
  scheduled_date: zodDateString('DateISO'),
  actual_date: zodDateString('DateTimeISO'),
});

//
// LoadDeliveryEditDTO
//

export type LoadDeliveryEditDTO = z.infer<typeof loadDeliveryEditSchema>;
export const loadDeliveryEditSchema = z.object({
  notes: zodString,
  venue: loadVenueSchema.prefault({}),
  save_delivery_contact: zodBoolean,
  scheduled_date: zodDateString('DateISO'),
  actual_date: zodDateString('DateTimeISO'),
});

//
// LoadCustomerEditDTO
//

export type LoadCustomerEditDTO = z.infer<typeof loadCustomerEditSchema>;
export const loadCustomerEditSchema = z.object({
  venue: loadVenueSchema.prefault({}),
  save_customer_contact: zodBoolean,
});

//
// LoadVehicleCreateDTO
//

export type LoadVehicleCreateDTO = z.infer<typeof loadVehicleCreateSchema>;
export const loadVehicleCreateSchema = loadVehicleSchema.extend({
  guid: zodOmit,
  load_guid: zodString,
});

//
// LoadVehicleEditDTO
//

export function isEmptyLoadVehicleDTO({
  guid,
  type,
  is_edi,
  is_inoperable,
  requires_enclosed_trailer,
  ...values
}: Partial<LoadVehicleDTO>): boolean {
  return (
    !is_inoperable &&
    !requires_enclosed_trailer &&
    (!type || type === 'other') &&
    isEmptyRecord(values)
  );
}

export type LoadVehicleEditDTO = z.infer<typeof loadVehicleEditSchema>;
export const loadVehicleEditSchema = loadVehicleSchema.extend({
  load_guid: zodString,
});

//
// LoadExpenseEditDTO
//

export function isEmptyLoadExpenseEditDTO({
  type,
  load_guid,
  show_on_invoice,
  deduct_from_driver_pay,
  ...values
}: LoadExpenseEditDTO) {
  return (
    type !== 'other' &&
    !show_on_invoice &&
    !deduct_from_driver_pay &&
    isEmptyRecord(values)
  );
}

export type LoadExpenseEditDTO = z.infer<typeof loadExpenseEditSchema>;
export const loadExpenseEditSchema = loadExpenseSchema.extend({
  load_guid: zodString,
});

//
// LoadBOLSendDTO
//

export type LoadBOLTemplate = (typeof LOAD_BOL_TEMPLATES)[number];
export const LOAD_BOL_TEMPLATES = [
  'default',
  'metrogistics',
  'multiple-units',
] as const;
export function formatLoadBOLTemplate(template: LoadBOLTemplate): string {
  switch (template) {
    default:
    case 'default':
      return 'Default';
    case 'metrogistics':
      return 'Metrogistics';
    case 'multiple-units':
      return 'Multiple units';
  }
}

export type LoadBOLSendDTO = z.infer<typeof loadBOLSendSchema>;
export const loadBOLSendSchema = z
  .object({
    email: zodString,
    use_broker_info: zodBoolean,
    template: zodString.prefault('default'),
  })
  .check(({ value, issues }) => {
    if (value.email && !z.email().safeParse(value.email).success) {
      issues.push({
        code: 'custom',
        input: value.email,
        path: ['email'],
        message: 'Enter a valid email',
      });
    }
  });

export function zodBolTemplate(templateKeys: readonly string[]) {
  const fallback = templateKeys[0] ?? 'default';

  return z
    .string()
    .nullish()
    .transform((value) =>
      value != null && templateKeys.includes(value) ? value : fallback,
    );
}

export function isEmptyLoadPayment(
  payment: Partial<LoadPaymentDTO | undefined>,
): boolean {
  if (payment) {
    const { method, invoice, price, ...rest } = payment;
    return (
      method === 'other' &&
      isEmptyRecord(rest) &&
      !price &&
      isEmptyRecord(invoice)
    );
  }
  return true;
}

export type LoadSendInvoiceDTO = z.infer<typeof sendInvoiceSchema>;
const customerSchema = z.object({
  name: zodRequiredString('Enter Customer Name'),
  contacts: z.array(contactSchema).prefault([]),
});

export const sendInvoiceSchema = z
  .object({
    customer: customerSchema.prefault({}),
    invoice_id: zodMaxString(
      50,
      'Maximum length for Invoice ID is 50 characters',
    ),
    invoice_date: zodRequiredDateString('DateTimeISO', 'Enter Invoice Date'),
    orders: z.array(orderLoadSchema).min(1, 'Enter Load Id').prefault([]),
    bol_template: zodString,
    email: zodBoolean,
    factor_this_invoice: zodBoolean,
    emails: z.array(contactSchema).prefault([]),
    is_carrier_requested_superpay: zodBoolean,
  })
  .check(({ value, issues }) => {
    if (!value.email) {
      return;
    }

    if (value.emails.length === 0) {
      issues.push({
        code: 'custom',
        input: value.emails,
        path: ['emails'],
        message: 'Select at least one email',
      });
      return;
    }

    const hasInvalidEmail = value.emails.some(
      ({ email }) => !email || !z.email().safeParse(email).success,
    );

    if (hasInvalidEmail) {
      issues.push({
        code: 'custom',
        input: value.emails,
        path: ['emails'],
        message: 'Enter valid email',
      });
    }
  });

export type SendQuickBooksDTO = z.infer<typeof sendQuickBooksSchema>;
export const sendQuickBooksSchema = z.object({
  customer_name: zodString,
  invoice_date: zodString,
  invoice_number: zodString,
  order_guids: z.array(zodString).prefault([]),
});

export type SendInvoiceEmailDTO = z.infer<typeof sendInvoiceEmailSchema>;
export const sendInvoiceEmailSchema = z.object({
  bol_template: zodString,
  customer_name: zodString,
  email: zodString,
  factor_this_invoice: zodBoolean,
  invoice_date: zodString,
  invoice_number: zodString,
  load_guids: z.array(zodString).prefault([]),
  attachments: z.array(zodNumber).prefault([]),
  is_carrier_requested_superpay: zodBoolean,
});
